import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchPublicDisplay } from './api';
import { DisplayCounterView } from './DisplayCounterView';
import { DisplayGroupView } from './DisplayGroupView';
import { useCallAnnouncer } from './useCallAnnouncer';
import { useDisplayLive } from './useDisplayLive';

function BoardMessage({
  title,
  body,
}: {
  title: string;
  body?: string;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-black p-8 text-center text-white">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {body ? <p className="text-white/60">{body}</p> : null}
    </div>
  );
}

/**
 * Public, full-screen queue board. Rendered outside the authenticated shell;
 * authenticated solely by the signed `t` token in the query string. The
 * layout kind selects between the single-counter LCD view and the group view.
 */
export function QmsDisplayBoardPage(): JSX.Element {
  const { t, i18n } = useTranslation('qms');
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const token = params.get('t');

  const query = useQuery({
    queryKey: ['qms-public-display', slug, token],
    queryFn: () => fetchPublicDisplay(slug ?? '', token ?? ''),
    enabled: Boolean(slug && token),
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const board = query.data ?? null;
  const live = useDisplayLive(board?.counters ?? []);
  useCallAnnouncer(live, (code, counter) => ({
    text: t('displays.public.announce', { code, counter }),
    lang: i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US',
  }));

  if (!token) {
    return (
      <BoardMessage
        title={t('board.error_title')}
        body={t('board.missing_token')}
      />
    );
  }
  if (query.isLoading) {
    return <BoardMessage title={t('board.loading')} />;
  }
  if (query.isError || !board) {
    return (
      <BoardMessage
        title={t('board.error_title')}
        body={t('board.error_body')}
      />
    );
  }

  const view =
    board.layout?.kind === 'counter' ? (
      <DisplayCounterView board={board} live={live} />
    ) : (
      <DisplayGroupView board={board} live={live} />
    );
  return view;
}
